import { AgencyModel } from "./agency.model";
import { AuthUser } from "./authuser.model";
import { DepartmentModel } from "./department.model";
import { LanguagesModel } from "./languages.model";
import { PositionModel } from "./position.model";
import { RolModel } from "./role.model";

export class UserModel {
  id!: number;
  is_active!: boolean;
  is_dev!: boolean;
  role_id?: number;
  username?: string;
  first_name!: string;
  middle_name?: string | null;
  last_name!: string;
  date_of_birth!: string; 
  gender!: string;
  language_id!: number;
  timezone?: string | null;
  timezone_mode?: 'automatic' | 'manual' | null;
  address?: string;
  address_2?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  zip_code?: string |null;
  email!: string;
  phone_number?: string | null;
  profile_picture?: string | null;
  position_id!: number;
  department_id!: number;
  agency_id!: number;
  manager_id!: number | null;
  monthly_salary?: number | null;
  last_login_date!: string | null;
  failed_attempts?: number;
  last_access_ip?: string | null;
  email_verified_at?: string | null;
  created_at!: string; 
  updated_at!: string;
  role?: RolModel;
  department?: DepartmentModel;
  agency?: AgencyModel;
  position?: PositionModel;
  language?: LanguagesModel;

  constructor(data: Partial<UserModel>) {
    Object.assign(this, data);

     // ✅ normaliza tipos conflictivos
    this.is_active = this.toBool((data as any)?.is_active);
    this.is_dev    = this.toBool((data as any)?.is_dev);

    // ✅ Limpieza útil
    this.email = (this.email ?? '').trim();
    //this.profile_picture = this.profile_picture || 'assets/icons/user-default.svg';
  
  }

   private toBool(v: any): boolean {
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number')  return v === 1;
    if (typeof v === 'string')  return ['1','true','yes'].includes(v.trim().toLowerCase());
    return false;
  }

  getFullName(): string {
    return `${this.first_name} ${this.last_name}`;
  }

  isActive(): string {
    if(this.failed_attempts === 3 && !this.is_active){
      return "Blocked"; 
    }
    return this.is_active === true ? "Active" : "Inactive";
  }

  getFormattedLastLogin(): string | null {
    if (!this.last_login_date) return null;
    const date = new Date(this.last_login_date);
    //return date.toLocaleString();
     return date.toLocaleString('en-EN', {
      year: 'numeric',
      month: 'long',   // 'numeric', '2-digit', 'short', 'long'
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      //second: '2-digit',
    });
  }

  getFormattedLastUpdate(): string | null {
    if (!this.updated_at) return null;
    const date = new Date(this.updated_at);
    //return date.toLocaleString();
     return date.toLocaleString('en-EN', {
      year: 'numeric',
      month: 'long',   // 'numeric', '2-digit', 'short', 'long'
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      //second: '2-digit',
    });
  }

  getFormattedCreateDate(): string | null {
    if (!this.created_at) return null;
    const date = new Date(this.created_at);
    //return date.toLocaleString();
     return date.toLocaleString('en-EN', {
      year: 'numeric',
      month: 'long',   // 'numeric', '2-digit', 'short', 'long'
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      //second: '2-digit',
      });
  }

  getInitials(): string {
    /**trim() elimina espacios al inicio y final del string
      charAt(0) luego toma el primer carácter válido
      El ?. (optional chaining) previene errores con valores null/undefined */
    const safeGetChar = (s?: string) => s?.trim().charAt(0)?.toUpperCase() || '';
    const initials = safeGetChar(this.first_name) + safeGetChar(this.last_name);
    return initials || 'US';
  }


  toUpdatePayload(): Partial<UserModel> {
    return {
      is_active: this.is_active,
      role_id: this.role_id,
      username: this.username,
      first_name: this.first_name,
      middle_name: this.middle_name,
      last_name: this.last_name,
      date_of_birth: this.date_of_birth,
      gender: this.gender,
      language_id: this.language_id,
      address: this.address,
      address_2: this.address_2,
      city: this.city,
      state: this.state,
      country: this.country,
      zip_code: this.zip_code,
      position_id: this.position_id,
      email: this.email,
      phone_number: this.phone_number,
      profile_picture: this.profile_picture,
      department_id: this.department_id,
      agency_id: this.agency_id,
      manager_id: this.manager_id,
      monthly_salary: this.monthly_salary,
      last_login_date: this.last_login_date,
      failed_attempts: this.failed_attempts,
      last_access_ip: this.last_access_ip,
      email_verified_at: this.email_verified_at,
    };
  }

  toPasswordUpdate(newPassword: string, audit_description?: string): UserModel {
    // clona solo los campos actualizables
    const clone = new UserModel(this.toUpdatePayload());
    // agrega dinámicamente la contraseña (no forma parte del modelo público)
    (clone as any).password = String(newPassword ?? '').trim();
    // agrega audit_description solo si viene
    const note = audit_description?.trim();
    if (note) (clone as any).audit_description = note;
    return clone;
  }

  emptyUser(): Partial<UserModel> {
    return {
      first_name: 'nexus',
      last_name: 'system',
      profile_picture: ''
    };
  }


  toAuthUser(): AuthUser {
    return {
      id: this.id ?? '',
      first_name: this.first_name ?? '',
      last_name: this.last_name ?? '',
      email: this.email ?? '',
      profile_picture: this.profile_picture ?? null,
      language: this.language ?? null,
      agency: this.agency ?? null,
      timezone: this.timezone ?? null,
      timezone_mode: this.timezone_mode ?? null
    };
  }



  getAgencyName(): string {
    if(this.agency){
      return this.agency.name;
    }
    return "none";
  }

   getAgencyColor(): string {
    return (this.agency?.color ?? '').trim() || '#6c757d';
  }


  getDepartmentName(): string {
    if(this.department){
      return this.department.name;
    }
    return "none";
  }

  getPositionName(): string {
    if(this.position){
      return this.position.name;
    }
    return "none";
  }

  getRoleName(): string {
    if(this.role){
      return this.role.name;
    }
    return "none";
  }
  
}
/*
constructor(user: User) {
    Object.assign(this, user);
  }

  // 🟦 Devuelve el nombre completo del usuario
  getFullName(): string {
    const parts = [this.first_name, this.middle_name, this.last_name];
    return parts.filter(Boolean).join(' ');
  }

  // 🟩 Devuelve la fecha de nacimiento formateada
  getFormattedDOB(): string {
    return this.formatDate(this.date_of_birth);
  }

  // 🟧 Devuelve la fecha de último login formateada
  getFormattedLastLogin(): string {
    return this.formatDate(this.last_login_date);
  }

  // 🟪 Devuelve la fecha de última actualización formateada
  getFormattedLastUpdate(): string {
    return this.formatDate(this.updated_at);
  }

  // 🟥 Devuelve el salario formateado (si aplica)
  getFormattedSalary(): string {
    if (this.monthly_salary == null) return 'N/D';
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
    }).format(this.monthly_salary);
  }

  // Función auxiliar privada para formatear fechas
  private formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
}*/
